import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';

import { VersionsViewService, VersionsEditorService } from '@app/services/app/versions.service';

import { AppService } from '@app/services/app/app.service';
import { AccessVersion, AccessProject, AccessProjectKeys } from '@app/models/access';
import { AppRouterService } from '@app/services/app/router.service';
import { ProjectsEditorService } from '@app/services/app/projects.service';

import * as Objects from '@core/objects';
import { AccountSession } from '@core/objects/account-session';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';
import { VersionShare, UploadVersionShare, VersionShareEntryDetail, isFeedbackVersionShare, getVersionShareFiles } from '@core/models/version-share.model';
import { VersionShareFeedbackMessage } from '@core/models/version-share-message.model';

import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { InterfaceListenerService } from '@core/services/interface-listener/interface-listener.service';
import { VersionsService } from '@core/services/versions/versions.service';


@Component({
    selector: 'version-details-content',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class VersionsDetailsContentMainComponent implements OnInit, OnDestroy {
    protected readonly PLUGIN_SEND_FILES = 'share_transfer';
    protected readonly PLUGIN_FEEDBACK = 'share_feedback';
    protected readonly APP_FILECOMPARISON = 'filecomparison';
    protected readonly ACCESS_GLOBAL_XRAY = 'access_global_xray';
    protected readonly PLUGIN_APOLLO = 'apollo'; // transitional: also gate "Compare" on this until apollo is fully retired

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_ERROR = 2;

    public readonly MODAL_VIEW_NONE = 0;
    public readonly MODAL_VIEW_FILES_SIGNED_ERROR = 1

    @Input() project: Objects.Request = null;
    public version: Objects.Version = null;
    public modalView: number = this.MODAL_VIEW_NONE;

    public pluginsAccess: Array<string> = [];
    public appsAccess: Array<string> = [];
    public accessGlobalXray: boolean = true;
    public access: AccessVersion;
    public accessProject: AccessProject;
    public accessFeedbacks: boolean = null;
    // @deprecated oct-2026: legacy share feedback bubbles url call response, replaced by versionShareList
    // public feedbacks: Array<VersionFeedback> = null;
    // @deprecated oct-2026: legacy share feedback url call response, replaced by myShare
    // public share: any = null;
    // oct-2026: current user's open approval/feedback entry on this version (null when not a recipient)
    public myShare: VersionShareEntryDetail = null;
    public sharedFileIds: Array<number> = null;

    // protected uploadVersionShares: Array<UploadVersionShare> = null;
    public versionShareMessages: Array<VersionShareFeedbackMessage> = [];
    public versionShareList: Array<VersionShare> = [];
    // versionShareList starts empty, so its length cannot tell "not loaded yet" from "no shares"
    protected versionSharesLoaded: boolean = false;
    /*
    access_version_write_comments: boolean;
    access_version_read_comments: boolean;
    access_version_change_status: boolean;
    access_version_original_name: boolean;
    access_version_upload_no_file: boolean;
    access_version_edit_labels: boolean;
     */

    public menu: Array<ThreeDotsMenuItem> = [
        {
            title: "Download File",
            key: "download",
            icon: "download",
            order: 1
        },
        {
            title: "Open Preview in New Tab",
            key: "open-preview",
            icon: "see-details",
            order: 2
        },
    ];

    protected menuSendFiles: ThreeDotsMenuItem = {
        title: "Send",
        key: "send-files",
        icon: "send-files",
        order: 3
    };
    protected menuCompare: ThreeDotsMenuItem = {
        title: "Compare",
        key: "compare",
        icon: "compare",
        order: 4
    };
    protected menuComparison: ThreeDotsMenuItem = {
        title: "NEW Filecomparison",
        htmltitle: "<strong>NEW</strong> Filecomparison",
        key: "comparison",
        icon: "compare",
        order: 5
    };

    public status:number = this.STATUS_LOADING;
    protected subs: Subscription = new Subscription();

    constructor(
        private app: AppService,
        private urlSrv: UrlService,
        private fileDownloadSrv: FileDownloadService,
        private editorVersionSrv: VersionsEditorService,
        private viewVersionSrv: VersionsViewService,
        private projectEditorSrv: ProjectsEditorService,
        private notifSrv: NotificationsService,
        private appRouter: AppRouterService,
        private iListener: InterfaceListenerService,
        private coreVersionsService: VersionsService
    ) { }

    ngOnInit() {


        this.subs.add(this.viewVersionSrv.selectVersion().subscribe((version: Objects.Version)=> {
            this.version = version;
            this.initView();
        }));


        this.subs.add(this.projectEditorSrv.selectAccessAll().subscribe((access: AccessProject) => {
            if (access != null) {
                this.accessProject = access;
                this.initView();
            }
        }));

        this.subs.add(this.editorVersionSrv.selectAccessAll().subscribe((access: AccessVersion)=> this.access = access));

        this.subs.add(combineLatest([this.app.getSession(), this.app.getAccountSession()]).subscribe( ([session, accountSession]: [any, AccountSession]) => {
            this.pluginsAccess = session.options.plugins;
            // apps list now comes from the accounts-api session; fall back to the legacy session field
            // in case the (separate, parallel) accounts-api call hasn't resolved yet
            this.appsAccess = accountSession?.current?.apps ?? session.options.organization.apps;
            this.accessGlobalXray = session.options.access?.hasOwnProperty(this.ACCESS_GLOBAL_XRAY) ? session.options.access[this.ACCESS_GLOBAL_XRAY] : true;
            // this.updateMenu(this.menuSendFiles,this.checkPluginAccess(this.PLUGIN_SEND_FILES))
            this.updateMenu(this.menuCompare,this.checkCompareAccess());
            this.updateMenu(this.menuComparison,this.checkFilecomparisonAccess());
            this.accessFeedbacks = this.checkPluginAccess(this.PLUGIN_FEEDBACK);
            if(this.accessFeedbacks)
                this.initFeedbacks();
            else 
                this.initView();
        }));

        // @deprecated oct-2026: legacy share feedback url call, replaced by selectMyVersionShares
        // this.subs.add(this.viewVersionSrv.selectShareFeedback().subscribe((shareVersionFeedback: ShareVersionFeedback) => {
        //     // here comes the shareVersionFeedback with properties:
        //     if(shareVersionFeedback != null){
        //         this.share = shareVersionFeedback.share;
        //         if( this.share.entry &&
        //             this.share.entry.share &&
        //             this.share.entry.share.properties )
        //         {
        //             let filesProp = this.share.entry.share.properties.filter((p:any) => p.key == 'files');
        //             this.sharedFileIds = filesProp.length > 0 ? (filesProp[0]).value : [];
        //         }
        //     }
        // }));

        this.subs.add(this.viewVersionSrv.selectMyVersionShares().subscribe((myShares: Array<VersionShareEntryDetail>) => {
            // at most one open approval/feedback entry per user (an open upload-version can coexist with it)
            this.myShare = myShares?.find((ms: VersionShareEntryDetail) => isFeedbackVersionShare(ms)) ?? null;
            this.sharedFileIds = getVersionShareFiles(this.myShare);
        }));

    }

    initView(){
        if(this.version == null)
            return;

        if ((this.accessFeedbacks != null && !this.accessFeedbacks) || 
            (this.accessFeedbacks && this.versionSharesLoaded )
        ){
            this.setFilesMenu();
            this.status = this.STATUS_DONE;

        }
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    onMenuClick(menu: AmsFilesListItemMenuClick) {
        switch(menu.key) {
            case "download-comments": 
                this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILE_ANNOTATIONS_START, {id: this.project.id, details: { url: "/versions/"+this.version.id+"/feedbacks", amsfile: menu.file}});
                break;
            case "download":
                this.fileDownloadSrv.downloadVersionAsset(menu.file);
                break;
            case "download-signatures":
                // start notification to download compressed project files in zip
                this.coreVersionsService.getAssetWithSignatures$(this.version, menu.file)
                    .subscribe({
                        next: (fileSigned: Objects.File) => {
                            // TODO: downlaod with signatures what file is retuned from the API???
                            window.location.href = this.urlSrv.build('/files/'+fileSigned.id);
                        },
                        error: (error) => {
                            this.modalView = this.MODAL_VIEW_FILES_SIGNED_ERROR;
                        }
                    })
                break;
            case "open-preview":
                window.open(window.location.href +'/files/'+menu.file.id, '_blank');
                break;
            case "send-files":
                if (this.checkPluginAccess(this.PLUGIN_SEND_FILES))
                    this.appRouter.navigate('/p/transfer/'+this.project.id, { queryParams: { file: +menu.file.id, version: this.version.id} });
                break;
            case "compare":
                this.appRouter.navigate('/p/apollo/request/'+this.project.id);
                break;
            case "comparison":
                this.appRouter.navigate('/filecomparison/project/'+this.project.id+'/artwork/'+this.version.id+'/file/'+menu.file.uuid);
                break;
        }
    }

    // oct-2026: info emits the VersionShare that was approved/rejected/completed, which carries no version: the
    // listeners (files-notes, preview-file-main) match by version id, so the loaded version is passed, as in
    // onShareVersionStatusUpdated()
    public onFeedbackDone(share: VersionShare) : void {
        if(this.accessFeedbacks){
            this.iListener.triggerFeedbackDone(this.version);
            // @deprecated oct-2026: legacy share feedback bubbles url call, replaced by the two reloads below
            // this.viewVersionSrv.loadFeedbacks();
            // bubbles of every recipient (GET /versions/{vid}/shares); the current user's open entry is taken
            // from the same response, which closes the notes/messages access once the feedback is done
            this.viewVersionSrv.loadVersionShares();
        }
        else 
            this.initView();
    }

    public onShareVersionStatusUpdated(share: VersionShare){
        this.iListener.triggerFeedbackDone(this.version);
    }

    protected checkPluginAccess(plugin: string){
        return this.pluginsAccess.some((pa) => pa == plugin);
    }

    protected checkAppAccess(app: string){
        return this.appsAccess.some((a) => a == app);
    }

    protected checkCompareAccess(): boolean {
        return this.accessGlobalXray && this.checkPluginAccess(this.PLUGIN_APOLLO);
    }

    protected checkFilecomparisonAccess(): boolean {
        return this.accessGlobalXray && this.checkAppAccess(this.APP_FILECOMPARISON);
    }

    // this function check either remove or add item. based on subscribed events
    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }

    protected setFilesMenu() : void {
        this.updateMenu(this.menuSendFiles, this.checkPluginAccess(this.PLUGIN_SEND_FILES) && this.accessProject.hasOwnProperty(AccessProjectKeys.SEND_FILE) && this.accessProject[AccessProjectKeys.SEND_FILE]);
    }

    protected initFeedbacks(){
        // @deprecated oct-2026: legacy share feedback bubbles url call, replaced by selectVersionShares
        // this.subs.add(this.viewVersionSrv.selectFeedbacks().subscribe((feedbacks: Array<VersionFeedback>) => {
        //     this.feedbacks = feedbacks;
        //     this.initView();
        // }))
        this.subs.add(this.viewVersionSrv.selectVersionShares().subscribe((allShares:VersionShare[]) => {
            if(allShares != null){
                this.versionShareList = allShares;
                this.versionSharesLoaded = true;
                this.initView();
            }
        }));

        this.subs.add(this.viewVersionSrv.selectVersionShareMessages().subscribe((shareMessages: Array<VersionShareFeedbackMessage>) => {
            // Here we get the messages generated based on the already completed or closed version shares (bubbles)
            if (shareMessages != null ){
                // Compare each message's custom ID (UUID) to ensure not equality to update new ones
                if (this.versionShareMessages != null && 
                    (this.versionShareMessages.length === 0 || 
                        !this.versionShareMessages.every((prevMsg, index) => prevMsg.uuid === shareMessages[index]?.uuid))) {
                    
                    this.versionShareMessages = shareMessages;
                }
            }
        }));
    }

    public dismmisError(){
        this.modalView = this.MODAL_VIEW_NONE;
    }

}
